import { useEffect, useRef, useState } from "react";
import OperationsMetrics from "./OperationsMetrics";
import ReviewerRequestPhoto from "./ReviewerRequestPhoto";
import ReviewerHcWorkflow, { useReviewerHcWorkflow } from "./ReviewerHcWorkflow";
import { pendingHcRouting, pendingHcNames } from "./reviewer-hc-workflow";
import ApprovalSlaBadge, { compareApprovalUrgency } from "./ApprovalSlaBadge";
import { photoRemovalAvailability } from "./workflow-ui";
import { filterReviewerRequests, reviewerReportCsv, reviewerReportFilename } from "./reviewer-report";
import { reviewerPreviewSandboxEnabled } from "./auth";
import {
  cancelSubmission,
  checkPhotoQuality,
  decideApproval,
  getSubmission,
  getSubmissionHistory,
  getReviewerHistory,
  getSubmissionPhoto,
  getWorkflowIdentity,
  listApprovals,
  listReviewRequests,
  listMySubmissions,
  listOperations,
  newIdempotencyKey,
  requestPhotoRemoval,
  submitOwnPhoto,
} from "./workflow-api";

const REJECTION_REASONS = [
  ["photo_quality", "Photo quality is not sufficient"],
  ["background", "Background does not meet requirements"],
  ["positioning", "Face position or crop needs adjustment"],
  ["appearance_policy", "Photo does not meet profile-photo requirements"],
  ["wrong_person", "Photo does not match the employee"],
  ["removal_not_approved", "Profile photo removal is not approved"],
  ["other", "Other reason"],
];

const QUALITY_WARNINGS = {
  too_dark: "The image may be too dark.",
  too_bright: "The image may be overexposed.",
  low_contrast: "The image has very low contrast.",
  low_detail: "The image contains unusually little visual detail.",
  possible_blur: "The image may be noticeably blurred.",
};

const rejectionLabel = (code) => REJECTION_REASONS.find(([value]) => value === code)?.[1] || code || "";

function message(error) {
  return error instanceof Error ? error.message : String(error);
}

const isRemoval = (submission) => submission?.requestType === "PhotoRemoval";
const requestLabel = (submission) => isRemoval(submission) ? "Remove profile photo" : "Update profile photo";

function formatDate(value) {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString([], {
    year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

function requestAge(value) {
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) return "";
  const hours = Math.max(0, Math.floor((Date.now() - timestamp) / 3_600_000));
  if (hours < 24) return `${hours}h old`;
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h old`;
}

function timelineLabel(event) {
  if (["SubmissionCreated", "RemovalRequestCreated"].includes(event.type)) return event.type === "RemovalRequestCreated" ? "Photo removal requested" : "Request submitted";
  if (event.type === "HCRoutingSelected") return event.outcome === "Central fallback HC" ? "Central HC fallback selected" : "Company HC route selected";
  if (event.type === "HCRoutingMissing") return "HC routing requires support";
  if (event.type === "HCRoutingRecovered") return "HC routing restored";
  if (event.type === "HCDecision") return ["Pending CCD", "Approved - Ready for Graph Update"].includes(event.newStatus) ? "HC approved" : "HC rejected";
  if (event.type === "CCDDecision") return event.newStatus === "Approved - Ready for Graph Update" ? "CCD approved" : "CCD rejected";
  if (event.type === "SubmissionCancelled") return "Request cancelled";
  if (event.type === "ApprovalReminderSent") return event.outcome || "Approval reminder sent";
  if (event.type === "ApprovalExpired") return "Request expired without approval";
  if (event.type === "GraphPhotoUpdated") return "Microsoft 365 photo updated";
  if (event.type === "GraphPhotoRemoved") return "Microsoft 365 photo removed";
  if (event.type === "PhotoLibraryDownloaded") return "Photo downloaded from library";
  if (event.type === "PhotoLibraryPreviewed") return "Photo viewed in library";
  if (event.type === "PhotoLibraryVersionExpired") return "Replaced photo retention expired";
  if (["GraphPhotoUpdateFailed", "GraphPhotoRemovalFailed", "UpdateFailed"].includes(event.type)) return "Microsoft 365 photo action failed";
  if (event.type === "DirectUpdateSubmitted") return "Direct update submitted";
  if (event.type === "DirectUpdateReasonRecorded") return "Reason recorded";
  if (event.type === "NotificationSent") return "Notification sent";
  const fallback = String(event.type || "Workflow event")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim();
  return fallback || "Workflow event";
}

const canResubmit = (status) => ["Rejected by HC", "Rejected by CCD", "Cancelled by Employee", "Expired - No Approval"].includes(status);

function SubmissionTimeline({ submission, getAccessToken, refreshVersion = 0, reviewer = false }) {
  const [open, setOpen] = useState(reviewer);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const tokenProvider = useRef(getAccessToken);
  tokenProvider.current = getAccessToken;
  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true);
    setError("");
    (reviewer ? getReviewerHistory : getSubmissionHistory)(submission.id, () => tokenProvider.current())
      .then(result => { if (active) setEvents(result.events || []); })
      .catch(loadError => { if (active) setError(message(loadError)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [open, submission.id, submission.status, refreshVersion, reviewer]);

  async function toggle() {
    const next = !open;
    setOpen(next);
  }

  return <div className="submission-timeline-wrap">
    <button className="timeline-toggle" onClick={toggle} disabled={loading}>
      {loading ? "Loading log…" : open ? "Hide log" : "View log"}
    </button>
    {open && <div className="submission-timeline">
      {error && <small className="timeline-error">{error}</small>}
      {!loading && !error && !events.length && <small>No workflow events recorded yet.</small>}
      {events.map((event, index) => <div className="timeline-event" key={`${event.type}-${event.date || index}-${index}`}>
        <span className="timeline-marker" />
        <div className="timeline-content"><strong>{timelineLabel(event)}</strong>
          {event.actorRole !== 'System' && (event.actorName || event.actorEmail || event.actorRole) && <small className="timeline-person">{event.actorName || event.actorEmail || 'Identity unavailable'}{event.actionSource ? ` · ${event.actionSource}` : ''}</small>}
          {(event.comments || (event.type === 'DirectUpdateReasonRecorded' && event.outcome)) && <small className="timeline-comment">{event.comments || event.outcome}</small>}
          {['GraphPhotoUpdateFailed', 'GraphPhotoRemovalFailed', 'UpdateFailed'].includes(event.type) && event.outcome && event.outcome !== 'Success' && event.outcome !== event.comments && <small className="timeline-failure">{event.outcome}</small>}
          {event.date && <time>{formatDate(event.date)}</time>}
          <details className="timeline-details">
            <summary>Details</summary>
            {event.actorEmail && event.actorName && <small>Email: {event.actorEmail}</small>}
            {event.actorRole && <small>Role: {event.actorRole}</small>}
            {event.newStatus && <small>Status: {event.newStatus}</small>}
            {event.outcome && <small>Outcome: {event.outcome}</small>}
            {event.actorRole && event.actorRole !== 'System' && !event.actionSource && <small>Channel not recorded</small>}
            {event.actorIdentitySource === 'Directory lookup' && <small>Name resolved from the current directory</small>}
          </details>
        </div>
      </div>)}
    </div>}
  </div>;
}

function SubmissionStatus({ submission, getAccessToken, onChange, onCancelled, onResubmit }) {
  const [historyRefresh, setHistoryRefresh] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [refreshError, setRefreshError] = useState("");
  async function refresh() {
    setRefreshing(true);
    setRefreshError("");
    try {
      const result = await getSubmission(submission.id, getAccessToken);
      onChange(result.submission);
      setHistoryRefresh(value => value + 1);
    } catch (error) {
      setRefreshError(message(error));
    } finally {
      setRefreshing(false);
    }
  }
  async function cancel() {
    if (!window.confirm(`Cancel this ${isRemoval(submission) ? "profile photo removal" : "profile photo update"} request? Any pending HC/CCD approval will stop.`)) return;
    setCancelling(true);
    setRefreshError("");
    try {
      const result = await cancelSubmission(submission.id, getAccessToken);
      onCancelled(result.submission);
    } catch (error) {
      setRefreshError(message(error));
    } finally {
      setCancelling(false);
    }
  }
  return (
    <div className="submission-status" role="status">
      <span className="status-dot" />
      <div><strong>{submission.status}</strong><small>{requestLabel(submission)}</small><small>Reference: {submission.id}</small>
        {submission.hcRejectReason && <small>HC reason: {rejectionLabel(submission.hcRejectReason)}</small>}
        {submission.hcComments && <small>HC comments: {submission.hcComments}</small>}
        {submission.ccdRejectReason && <small>CCD reason: {rejectionLabel(submission.ccdRejectReason)}</small>}
        {submission.ccdComments && <small>CCD comments: {submission.ccdComments}</small>}
      </div>
      <div className="submission-status-actions">
        <button onClick={refresh} disabled={refreshing || cancelling}>{refreshing ? "Checking…" : "Refresh"}</button>
        {submission.canCancel && <button className="cancel-request" onClick={cancel} disabled={refreshing || cancelling}>{cancelling ? "Cancelling…" : "Cancel request"}</button>}
        {canResubmit(submission.status) && <button className="resubmit-request" onClick={onResubmit} disabled={refreshing || cancelling}>{isRemoval(submission) ? "Start new request" : "Fix & resubmit"}</button>}
      </div>
      <div className="submission-status-detail">
        <SubmissionTimeline submission={submission} getAccessToken={getAccessToken} refreshVersion={historyRefresh} />
      </div>
      {refreshError && <small className="status-refresh-error">{refreshError}</small>}
    </div>
  );
}

function RequestHistory({ currentId, getAccessToken, onCancelled }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cancellingId, setCancellingId] = useState("");
  const [error, setError] = useState("");

  async function load() {
    setLoading(true);
    setError("");
    try {
      const result = await listMySubmissions(getAccessToken);
      setItems(result.submissions || []);
    } catch (loadError) {
      setError(message(loadError));
    } finally {
      setLoading(false);
    }
  }

  async function toggle() {
    const next = !open;
    setOpen(next);
    if (next) await load();
  }

  async function cancelHistoryItem(item) {
    if (!window.confirm(`Cancel this ${isRemoval(item) ? "profile photo removal" : "profile photo update"} request? Any pending HC/CCD approval will stop.`)) return;
    setCancellingId(item.id);
    setError("");
    try {
      const result = await cancelSubmission(item.id, getAccessToken);
      setItems((current) => current.map((request) => request.id === item.id ? result.submission : request));
      if (item.id === currentId) onCancelled?.(result.submission);
    } catch (cancelError) {
      setError(message(cancelError));
    } finally {
      setCancellingId("");
    }
  }

  return <div className="request-history">
    <button className="request-history-toggle" onClick={toggle} disabled={loading || Boolean(cancellingId)}>
      {loading ? "Loading requests…" : open ? "Hide request history" : "View request history"}
    </button>
    {open && <div className="request-history-list">
      <div className="request-history-heading"><strong>My profile photo requests</strong><button onClick={load} disabled={loading || Boolean(cancellingId)}>Refresh</button></div>
      {error && <small className="timeline-error">{error}</small>}
      {!loading && !error && !items.length && <small>No previous requests found.</small>}
      {items.map((item) => <div className={item.id === currentId ? "request-history-item current" : "request-history-item"} key={item.id}>
        <div><strong>{item.status}</strong><small>{requestLabel(item)}</small><small>{formatDate(item.submittedDate)}</small><small>Reference: {item.id}</small></div>
        {item.canCancel && <div className="request-history-actions">
          <button className="request-history-cancel" onClick={() => cancelHistoryItem(item)} disabled={Boolean(cancellingId)}>
            {cancellingId === item.id ? "Cancelling…" : "Cancel request"}
          </button>
        </div>}
        <SubmissionTimeline submission={item} getAccessToken={getAccessToken} />
      </div>)}
    </div>}
  </div>;
}

function QualityResult({ quality }) {
  if (!quality) return null;
  const warnings = quality.warnings || [];
  return <div className={warnings.length ? "quality-result warning" : "quality-result passed"}>
    <strong>{warnings.length ? "Technical quality review recommended" : "Basic technical quality checks passed"}</strong>
    {warnings.map((code) => <small key={code}>{QUALITY_WARNINGS[code] || code}</small>)}
    <div className="quality-metrics">
      <span>Brightness {quality.metrics?.brightness ?? "–"}</span>
      <span>Contrast {quality.metrics?.contrast ?? "–"}</span>
      <span>Detail {quality.metrics?.entropy ?? "–"}</span>
      <span>Sharpness {quality.metrics?.sharpness ?? "–"}</span>
    </div>
    <small>This check is advisory only. HC/CCD still review identity, crop, background and policy compliance.</small>
  </div>;
}

function OperationsPanel({ getAccessToken }) {
  const [data, setData] = useState({ summary: { waitingForWorker: 0, updating: 0, failed: 0 }, submissions: [] });
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function load() {
    setLoading(true);
    setError("");
    try {
      const result = await listOperations(getAccessToken);
      setData(result);
    } catch (loadError) {
      setError(message(loadError));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  const visible = (data.submissions || []).filter((item) => {
    const q = search.trim().toLowerCase();
    const textMatch = !q || [item.employeeName, item.employeeEmail, item.companyName, item.id].some((value) => String(value || "").toLowerCase().includes(q));
    const statusMatch = status === "all"
      || (status === "ready" && item.status === "Approved - Ready for Graph Update")
      || (status === "updating" && item.status === "Updating Microsoft 365")
      || (status === "failed" && ["Update Failed", "Requires Support"].includes(item.status));
    return textMatch && statusMatch;
  });

  return <section className="operations-panel">
    <div className="workflow-subheading"><div><h3>Operations</h3><small>Read-only workflow diagnostics</small></div><button onClick={load} disabled={loading}>{loading ? "Loading…" : "Refresh"}</button></div>
    <div className="operations-summary">
      <span>Ready <strong>{data.summary?.waitingForWorker ?? 0}</strong></span>
      <span>Updating <strong>{data.summary?.updating ?? 0}</strong></span>
      <span>Failed <strong>{data.summary?.failed ?? 0}</strong></span>
    </div>
    <OperationsMetrics metrics={data.metrics} generatedAt={data.generatedAt} />
    <div className="operations-filters">
      <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search operations" aria-label="Search operations" />
      <select value={status} onChange={(event) => setStatus(event.target.value)} aria-label="Filter operations status">
        <option value="all">All operational states</option><option value="ready">Ready for worker</option><option value="updating">Updating</option><option value="failed">Failed / support</option>
      </select>
    </div>
    {error && <div className="workflow-error" role="alert">{error}</div>}
    {!loading && !error && !visible.length && <p className="workflow-empty">No operational requests match this view.</p>}
    <div className="operations-list">
      {visible.map((item) => <article className="operations-item" key={item.id}>
        <div className="operations-item-heading"><div><strong>{item.employeeName}</strong><small>{item.employeeEmail}</small></div><span>{item.status}</span></div>
        <div className="operations-meta"><span>{requestLabel(item)}</span><span>{item.companyName || "No company"}</span><span>{requestAge(item.submittedDate)}</span></div>
        <div className="operations-diagnostics">
          {item.supportReason && <small className="support-error">Support reason: {item.supportReason}</small>}
          <small>Retry count: {item.retryCount ?? 0}</small>
          {item.processingStartedDate && <small>Processing started: {formatDate(item.processingStartedDate)}</small>}
          {item.notificationStatus && <small>Notification: {item.notificationStatus}</small>}
          {item.graphResult && <small>Graph result: {item.graphResult}</small>}
          {item.graphError && <small className="support-error">Graph error: {item.graphError}</small>}
        </div>
        <SubmissionTimeline submission={item} getAccessToken={getAccessToken} />
      </article>)}
    </div>
    <small className="operations-note">No retry or Microsoft 365 mutation action is available here until the live update path passes Gate E.</small>
  </section>;
}

export function WorkflowDialog({ identity, getAccessToken, onClose = () => {}, standalone = false, initialSubmissionId = "" }) {
  const linkedRequest = useRef(initialSubmissionId);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [preview, setPreview] = useState(null);
  const [decodedPreview, setDecodedPreview] = useState(null);
  const [comments, setComments] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [companyFilter, setCompanyFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [hcWorkflowOpen, setHcWorkflowOpen] = useState(false);
  const reviewerMode = standalone && identity.capabilities.reviewer === true;
  const hcWorkflow = useReviewerHcWorkflow(getAccessToken, reviewerMode);
  const hcRoutingDisplay = hcWorkflow.data || (hcWorkflow.error ? { unavailable: true } : null);
  const selectedHcRoute = reviewerMode ? pendingHcRouting(selected, hcRoutingDisplay) : null;
  const approvalCapable = identity.capabilities.hcApprover || identity.capabilities.ccdApprover || identity.capabilities.support;
  const workflowCapable = reviewerMode || approvalCapable;

  const companies = [...new Set(items.map((item) => item.companyName).filter(Boolean))].sort();
  const reviewerStatuses = [...new Set(items.map((item) => item.status).filter(Boolean))].sort();
  const filteredItems = (reviewerMode
    ? filterReviewerRequests(items, {
        search,
        status: statusFilter,
        type: typeFilter,
        company: companyFilter,
        dateFrom,
        dateTo,
      })
    : items.filter((item) => {
        const q = search.trim().toLowerCase();
        const textMatch = !q || [item.employeeName, item.employeeEmail, item.companyName, item.id].some((value) => String(value || "").toLowerCase().includes(q));
        const statusMatch = statusFilter === "all"
          || (statusFilter === "hc" && item.status === "Pending HC")
          || (statusFilter === "ccd" && item.status === "Pending CCD")
          || (statusFilter === "support" && ["Update Failed", "Requires Support"].includes(item.status));
        const typeMatch = typeFilter === "all" || (typeFilter === "removal" ? isRemoval(item) : !isRemoval(item));
        const companyMatch = companyFilter === "all" || item.companyName === companyFilter;
        return textMatch && statusMatch && typeMatch && companyMatch;
      })).sort(compareApprovalUrgency);

  const counts = {
    hc: items.filter((item) => item.status === "Pending HC").length,
    ccd: items.filter((item) => item.status === "Pending CCD").length,
    support: items.filter((item) => ["Update Failed", "Requires Support"].includes(item.status)).length,
    total: items.length,
    active: items.filter((item) => ["Pending HC", "Pending CCD", "Approved - Ready for Graph Update", "Updating Microsoft 365"].includes(item.status)).length,
    completed: items.filter((item) => item.status === "Completed").length,
  };

  useEffect(() => {
    if (!reviewerMode) return;
    setSelected(current => filteredItems.find(item => item.id === current?.id) || filteredItems[0] || null);
  }, [items, search, statusFilter, typeFilter, companyFilter, dateFrom, dateTo, reviewerMode]);

  async function loadApprovals() {
    if (!workflowCapable) return;
    setBusy(true);
    setError("");
    try {
      const result = reviewerMode
        ? await listReviewRequests(getAccessToken)
        : await listApprovals(getAccessToken);
      let loaded = result.submissions || [];
      if (linkedRequest.current && !reviewerMode) {
        const id = linkedRequest.current;
        // Fetch through the normal authorized endpoint, including finished requests
        // that are no longer in the approval inbox. Never select another request on failure.
        const target = loaded.find(item => item.id === id) || (await getSubmission(id, getAccessToken)).submission;
        if (!target || target.id !== id) throw new Error("The linked request is unavailable.");
        if (!loaded.some(item => item.id === id)) loaded = [target, ...loaded];
        setItems(loaded);
        setSelected(target);
        linkedRequest.current = "";
      } else {
        setItems(loaded);
        setSelected((current) => loaded.find((item) => item.id === current?.id) || loaded[0] || null);
      }
    } catch (loadError) {
      setError(message(loadError));
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => { loadApprovals(); }, []);
  useEffect(() => {
    let active = true;
    let url = "";
    setPreview(null);
    setDecodedPreview(null);
    setRejectReason("");
    setComments("");
    if (!selected || reviewerMode) return undefined;
    if (isRemoval(selected)) {
      setPreview({ id: selected.id, removal: true });
      setDecodedPreview("removal");
      return undefined;
    }
    getSubmissionPhoto(selected.id, getAccessToken)
      .then((blob) => {
        if (!active) return;
        url = URL.createObjectURL(blob);
        setPreview({ id: selected.id, url });
      })
      .catch((previewError) => { if (active) setError(message(previewError)); });
    return () => { active = false; if (url) URL.revokeObjectURL(url); };
  }, [selected?.id]);

  const decisionReady = !reviewerMode && selected && (isRemoval(selected)
    ? preview?.id === selected.id && decodedPreview === "removal"
    : preview?.id === selected.id && decodedPreview === preview?.url);

  function exportReviewerReport() {
    if (!reviewerMode || !filteredItems.length) return;
    const blob = new Blob([reviewerReportCsv(filteredItems)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = reviewerReportFilename();
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    setNotice(`Exported ${filteredItems.length} filtered request${filteredItems.length === 1 ? "" : "s"} to CSV.`);
  }

  async function decide(decision) {
    if (!selected || !decisionReady) return;
    const stage = selected.status === "Pending HC" ? "hc" : selected.status === "Pending CCD" ? "ccd" : "";
    if (!stage) return;
    if (decision === "reject" && !rejectReason) {
      setError("Select a rejection reason before rejecting this request.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await decideApproval(selected.id, stage, decision, comments, decision === "reject" ? rejectReason : "", getAccessToken);
      setComments("");
      setRejectReason("");
      setNotice(`Decision recorded for ${selected.employeeName}.`);
      await loadApprovals();
    } catch (decisionError) {
      setError(message(decisionError));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={standalone ? "reviewer-portal-shell" : "workflow-backdrop"} role={standalone ? undefined : "presentation"} onMouseDown={(event) => { if (!standalone && event.target === event.currentTarget) onClose(); }}>
      <section className={standalone ? "workflow-dialog reviewer-portal-dialog" : "workflow-dialog"} role={standalone ? "region" : "dialog"} aria-modal={standalone ? undefined : "true"} aria-labelledby="workflow-title">
        <div className="workflow-dialog-heading"><div><p className="eyebrow">{reviewerMode ? "Read-only review" : "Authorized workflow"}</p><h2 id="workflow-title">{reviewerMode ? "Reviewer portal" : "Profile photo tasks"}</h2>{reviewerMode && <small>Request register, audit log and filtered reporting</small>}</div>{!standalone && <button onClick={onClose} aria-label="Close">×</button>}</div>
        {error && <div className="workflow-error" role="alert">{error}</div>}
        {notice && <div className="workflow-notice" role="status">{notice}</div>}
        {reviewerMode && reviewerPreviewSandboxEnabled && !standalone && <div className="workflow-notice" role="status"><strong>Preview environment</strong> — Real Production workflow data is shown read-only. Preview write actions are disabled and Microsoft 365 is not modified.</div>}

        {workflowCapable && <>
          <div className="approval-summary">
            {reviewerMode ? <>
              <span>All requests <strong>{counts.total}</strong></span>
              <span>Active <strong>{counts.active}</strong></span>
              <span>Completed <strong>{counts.completed}</strong></span>
            </> : <>
              <span>HC pending <strong>{counts.hc}</strong></span>
              <span>CCD pending <strong>{counts.ccd}</strong></span>
              <span>Support <strong>{counts.support}</strong></span>
            </>}
          </div>
          <div className="approval-filters">
            <input aria-label="Search approval requests" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search employee, email, company or reference" />
            <select aria-label="Filter request status" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
              <option value="all">All statuses</option>
              {reviewerMode
                ? reviewerStatuses.map((status) => <option value={status} key={status}>{status}</option>)
                : <><option value="hc">Pending HC</option><option value="ccd">Pending CCD</option><option value="support">Needs support</option></>}
            </select>
            <select aria-label="Filter request type" value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)}>
              <option value="all">All request types</option><option value="update">Photo update</option><option value="removal">Photo removal</option>
            </select>
            <select aria-label="Filter company" value={companyFilter} onChange={(event) => setCompanyFilter(event.target.value)}>
              <option value="all">All companies</option>{companies.map((company) => <option value={company} key={company}>{company}</option>)}
            </select>
          </div>
          {reviewerMode && <div className="reviewer-date-filters">
            <div><strong>Submission date</strong><small>Saudi Arabia time · includes both dates</small></div>
            <label>From<input type="date" value={dateFrom} max={dateTo || undefined} onChange={event => setDateFrom(event.target.value)} /></label>
            <label>To<input type="date" value={dateTo} min={dateFrom || undefined} onChange={event => setDateTo(event.target.value)} /></label>
            <button type="button" onClick={() => { setSearch(""); setStatusFilter("all"); setTypeFilter("all"); setCompanyFilter("all"); setDateFrom(""); setDateTo(""); }}>Clear filters</button>
            {dateFrom && dateTo && dateFrom > dateTo && <p role="alert">The end date must be on or after the start date.</p>}
          </div>}
          {reviewerMode && <div className="reviewer-report-bar">
            <span><strong>{filteredItems.length}</strong> of {items.length} requests in the current filtered view</span>
            <button type="button" onClick={exportReviewerReport} disabled={!filteredItems.length}>Export filtered CSV ({filteredItems.length})</button>
          </div>}
          {reviewerMode && <div className="reviewer-hc-toolbar">
            <div><strong>HC approval ownership</strong><small>See which HC approvers can act for each Business Unit.</small></div>
            <button type="button" aria-controls="reviewer-hc-workflow" aria-expanded={hcWorkflowOpen} onClick={() => setHcWorkflowOpen(open => !open)}>{hcWorkflowOpen ? "Hide HC workflow" : "View HC workflow"}</button>
          </div>}
          {reviewerMode && hcWorkflowOpen && <div id="reviewer-hc-workflow"><ReviewerHcWorkflow workflow={hcWorkflow} /></div>}
          <div className={reviewerMode ? "reviewer-register-layout" : "approval-layout"}>
            {reviewerMode ? <div className="reviewer-register">
              <div className="workflow-subheading"><h3>Request register</h3><button onClick={loadApprovals} disabled={busy}>{busy ? "Refreshing…" : "Refresh"}</button></div>
              <div className="reviewer-table-scroll" tabIndex={0} role="region" aria-label="Request register">
                <table className="reviewer-table">
                  <caption className="reviewer-table-caption">Select an employee to view request details and the recorded workflow log.</caption>
                  <thead><tr><th scope="col">Employee</th><th scope="col">Company / request</th><th scope="col">Status / submitted</th><th scope="col">Pending with HC</th></tr></thead>
                  <tbody>{filteredItems.map(item => <tr key={item.id} className={selected?.id === item.id ? "is-selected" : ""}>
                    <td><button type="button" aria-pressed={selected?.id === item.id} onClick={() => setSelected(item)}>{item.employeeName || item.employeeEmail}</button><small>{item.employeeEmail}</small></td>
                    <td>{item.companyName || "No company"}<small>{requestLabel(item)}</small></td>
                    <td><span className="reviewer-status" data-status={item.status}>{item.status}</span><small>{formatDate(item.submittedDate)}</small></td>
                    <td className="reviewer-hc-cell">{item.status === "Pending HC" ?
                      <><strong>{pendingHcNames(item, hcRoutingDisplay)}</strong>
                        {!hcRoutingDisplay?.unavailable && pendingHcRouting(item, hcRoutingDisplay)?.approvers?.length > 0 &&
                          <small>{pendingHcRouting(item, hcRoutingDisplay)?.status === "central_fallback" ? "Central HC fallback" : "Any listed HC approver may act if authorized"}</small>}</> :
                      <span className="reviewer-hc-empty">—</span>}</td>
                  </tr>)}</tbody>
                </table>
                {!filteredItems.length && <p className="workflow-empty">{busy ? "Loading requests…" : "No requests match these filters. Try another search or clear the filters."}</p>}
              </div>
            </div> : <div className="approval-list">
              <div className="workflow-subheading"><h3>{reviewerMode ? "All requests" : "Approval inbox"}</h3><button onClick={loadApprovals} disabled={busy}>Refresh</button></div>
              {!filteredItems.length && <p className="workflow-empty">{busy ? "Loading requests…" : "No requests match these filters."}</p>}
              {filteredItems.map((item) => <button key={item.id} className={selected?.id === item.id ? "approval-item active" : "approval-item"} onClick={() => setSelected(item)}>
                <strong>{item.employeeName}</strong><span>{item.status}</span><small>{requestLabel(item)} · {requestAge(item.submittedDate)}</small><small>{item.companyName || "No company"}</small><small>{item.employeeEmail}</small>
                {!reviewerMode && <ApprovalSlaBadge submission={item} compact />}
              </button>)}
            </div>}
            <div className={reviewerMode ? "approval-detail reviewer-detail" : "approval-detail"}>
              {selected ? <>
                {reviewerMode
                  ? <div className="reviewer-detail-heading"><p className="eyebrow">Request details</p><span className="reviewer-status" data-status={selected.status}>{selected.status}</span></div>
                  : isRemoval(selected)
                    ? <div className="removal-request-preview"><strong>Remove profile photo</strong><span>No replacement image is attached to this request.</span></div>
                    : preview?.id === selected.id
                      ? <img src={preview.url} onLoad={() => setDecodedPreview(preview.url)} onError={() => setDecodedPreview(null)} alt={`Submitted profile for ${selected.employeeName}`} />
                      : <div className="preview-loading">Loading photo…</div>}
                <h3>{selected.employeeName}</h3><p>{selected.employeeEmail}</p>
                {reviewerMode ? <dl className="reviewer-facts"><div><dt>Company</dt><dd>{selected.companyName || "No company"}</dd></div><div><dt>Request type</dt><dd>{requestLabel(selected)}</dd></div><div><dt>Submitted</dt><dd>{formatDate(selected.submittedDate)}</dd></div><div><dt>Reference</dt><dd className="reviewer-reference">{selected.id}</dd></div></dl> : <div className="approval-facts"><span>{selected.status}</span><span>{selected.companyName || "No company"}</span><span>{requestLabel(selected)}</span><span>{formatDate(selected.submittedDate)}</span></div>}
                {!reviewerMode && <ApprovalSlaBadge submission={selected} />}
                {reviewerMode && selected.status === "Pending HC" && <section className="reviewer-pending-hc">
                  <h4>Pending with HC</h4>
                  {selectedHcRoute?.approvers?.length ?
                    <ul>{selectedHcRoute.approvers.map((person, index) => <li key={person.email || index}><strong>{person.name}</strong>{person.email && <small>{person.email}</small>}</li>)}</ul> :
                    <p>{pendingHcNames(selected, hcRoutingDisplay)}</p>}
                  {selectedHcRoute?.status === "central_fallback" && <small>Central HC fallback is handling this BU.</small>}
                  {selectedHcRoute?.approvers?.length > 0 && <small>These are the configured HC routing contacts. HC.Approver permission is also required; no single person is the exclusive owner.</small>}
                  <button type="button" onClick={() => setHcWorkflowOpen(true)}>View HC workflow for all BUs</button>
                </section>}
                {reviewerMode && <>
                  <ReviewerRequestPhoto key={`photo-${selected.id}`} submission={selected} getAccessToken={getAccessToken} />
                  <h4 className="reviewer-log-heading">Workflow log</h4>
                  <SubmissionTimeline key={`log-${selected.id}`} reviewer submission={selected} getAccessToken={getAccessToken} refreshVersion={items} />
                  <details className="reviewer-access-note"><summary>Read-only request review</summary><p>Reviewer access includes this request’s submitted photo and workflow log. Approval actions and Original/Applied library downloads require their separate roles.</p></details>
                </>}
                {["Update Failed", "Requires Support"].includes(selected.status) && <div className="support-diagnostic">
                  <strong>Operations detail</strong>
                  <span>Retry count: {selected.retryCount ?? 0}</span>
                  {selected.processingStartedDate && <span>Processing started: {formatDate(selected.processingStartedDate)}</span>}
                  {selected.graphError && <span className="support-error">{selected.graphError}</span>}
                  <small>Visibility only. Retry actions remain disabled until the live Microsoft 365 update path is validated.</small>
                </div>}
                {!reviewerMode && ["Pending HC", "Pending CCD"].includes(selected.status) && <>
                  <label htmlFor="approval-reason">Rejection reason</label>
                  <select id="approval-reason" value={rejectReason} onChange={(event) => setRejectReason(event.target.value)}>
                    <option value="">Select only when rejecting</option>{REJECTION_REASONS.map(([value, label]) => <option value={value} key={value}>{label}</option>)}
                  </select>
                  <label htmlFor="approval-comments">Decision comments</label>
                  <textarea id="approval-comments" maxLength="1000" value={comments} onChange={(event) => setComments(event.target.value)} placeholder="Optional for approval; add useful details for a rejection." />
                  <div className="decision-buttons"><button className="reject" disabled={busy || !decisionReady || !rejectReason} onClick={() => decide("reject")}>Reject</button><button className="approve" disabled={busy || !decisionReady} onClick={() => decide("approve")}>Approve</button></div>
                </>}
              </> : <p className="workflow-empty">{reviewerMode ? "Select a request." : "Select an approval task."}</p>}
            </div>
          </div>
        </>}

        {identity.capabilities.support && <OperationsPanel getAccessToken={getAccessToken} />}

        {!workflowCapable && <p className="workflow-empty">Your account has no HC, CCD, reviewer, or support tasks.</p>}
      </section>
    </div>
  );
}

export default function WorkflowPanel({ photoReady, filename, renderPhoto, originalPhoto, getAccessToken, confirmShoulderCrop = async () => true }) {
  const keyRef = useRef(newIdempotencyKey());
  const removalKeyRef = useRef(newIdempotencyKey());
  const [submission, setSubmission] = useState(null);
  const [identity, setIdentity] = useState(null);
  const [quality, setQuality] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [linkedSubmissionId, setLinkedSubmissionId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const removalAvailability = photoRemovalAvailability(submission);
  const activeRequest = removalAvailability.disabled;

  useEffect(() => {
    let active = true;
    const linkId = new URLSearchParams(window.location.search).get("submission");
    const id = linkId || sessionStorage.getItem("profile-submission");
    if (id) getSubmission(id, getAccessToken)
      .then(async (result) => {
        if (!active) return;
        setSubmission(result.submission);
        if (!linkId) return;
        const access = await getWorkflowIdentity(getAccessToken);
        if (!active) return;
        const capabilities = access.identity?.capabilities || {};
        if (capabilities.hcApprover || capabilities.ccdApprover || capabilities.support) {
          setIdentity(access.identity);
          setLinkedSubmissionId(linkId);
          setDialogOpen(true);
        }
      })
      .catch((loadError) => { if (active) { sessionStorage.removeItem("profile-submission"); setError(message(loadError)); } });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    keyRef.current = newIdempotencyKey();
    setQuality(null);
    setError("");
    setNotice("");
  }, [filename]);

  useEffect(() => {
    if (submission && !activeRequest) {
      keyRef.current = newIdempotencyKey();
      removalKeyRef.current = newIdempotencyKey();
    }
  }, [submission?.id, submission?.status, activeRequest]);

  async function submit() {
    // Sideways shoulder crops are a local advisory, separate from API quality
    // checks. No request is created when the employee chooses Replace photo.
    if (!(await confirmShoulderCrop())) {
      setNotice("Submission paused. Replace the cropped photo or review the portrait before trying again.");
      return;
    }
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await getAccessToken();
      const blob = await renderPhoto();
      const preflight = await checkPhotoQuality(blob, getAccessToken);
      setQuality(preflight.quality);
      if (preflight.quality?.warnings?.length) {
        const details = preflight.quality.warnings.map((code) => QUALITY_WARNINGS[code] || code).join("\n• ");
        const proceed = window.confirm(`Technical quality check found:\n\n• ${details}\n\nThis is advisory only. Continue and submit for HC/CCD review?`);
        if (!proceed) {
          setNotice("Submission paused so you can adjust the photo. The quality check is advisory and did not create a request.");
          return;
        }
      }
      const result = await submitOwnPhoto(blob, getAccessToken, keyRef.current, originalPhoto);
      setSubmission(result.submission);
      sessionStorage.setItem("profile-submission", result.submission.id);
    } catch (submitError) {
      setError(message(submitError));
    } finally {
      setBusy(false);
    }
  }

  async function submitRemoval() {
    if (!window.confirm("Request removal of your current Microsoft 365 profile photo? This will still require HC and CCD approval.")) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const result = await requestPhotoRemoval(getAccessToken, removalKeyRef.current);
      setSubmission(result.submission);
      sessionStorage.setItem("profile-submission", result.submission.id);
    } catch (removeError) {
      setError(message(removeError));
    } finally {
      setBusy(false);
    }
  }

  function clearSubmissionForResubmit() {
    sessionStorage.removeItem("profile-submission");
    const url = new URL(window.location.href);
    if (url.searchParams.has("submission")) {
      url.searchParams.delete("submission");
      window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
    }
    keyRef.current = newIdempotencyKey();
    removalKeyRef.current = newIdempotencyKey();
    setSubmission(null);
    setQuality(null);
    setError("");
    setNotice("Ready for a new request. Update the photo or request removal when ready.");
  }

  function handleCancelled(cancelled) {
    setSubmission(cancelled);
    setError("");
    setNotice("Request cancelled. The approval chain has stopped and you can start a new request.");
  }

  async function openTools() {
    setBusy(true);
    setError("");
    try {
      const result = await getWorkflowIdentity(getAccessToken);
      setIdentity(result.identity);
      setDialogOpen(true);
    } catch (toolError) {
      setError(message(toolError));
    } finally {
      setBusy(false);
    }
  }

  return <>
    <div className="panel-section workflow-section">
      <div className="step-number">03</div><h2>Submit for approval</h2>
      <p>Submit a new profile photo or request removal of your current photo. Both paths require HC and CCD approval and are fully audited.</p>
      {submission && <SubmissionStatus submission={submission} getAccessToken={getAccessToken} onChange={setSubmission} onCancelled={handleCancelled} onResubmit={clearSubmissionForResubmit} />}
      <div className="submission-choice-actions">
        <button className="submit-button" disabled={!photoReady || busy || activeRequest} onClick={submit}>{busy ? "Checking…" : "Submit photo for approval"}</button>
        <button className="remove-photo-button" disabled={busy || activeRequest} onClick={submitRemoval}>{busy ? "Working…" : "Request photo removal"}</button>
      </div>
      {removalAvailability.reason && <div className="workflow-notice" role="note">{removalAvailability.reason}</div>}
      {!activeRequest && <QualityResult quality={quality} />}
      <RequestHistory currentId={submission?.id} getAccessToken={getAccessToken} onCancelled={handleCancelled} />
      <button className="workflow-tools-button" disabled={busy} onClick={openTools}>Open authorized workflow tasks</button>
      {notice && <div className="workflow-notice" role="status">{notice}</div>}
      {error && <div className="workflow-error" role="alert">{error}</div>}
    </div>
    {dialogOpen && identity && <WorkflowDialog initialSubmissionId={linkedSubmissionId} identity={identity} getAccessToken={getAccessToken} onClose={() => { setDialogOpen(false); setLinkedSubmissionId(""); }} />}
  </>;
}
